/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { TCreateSessionParams } from "@tivocorp/data-domain-lib";

import { DeviceModel } from "../../device";
import { SessionInteractor } from "../../interactor";
import { PlaybackContentType, SessionState } from "../enums";
import type { ISessionCreateModel } from "../interfaces";
import { StreamingSessionImpl } from "./StreamingSessionImpl";

/**
 * Streaming session implementation for SOCU content types:
 * STARTOVER_BEGINNING, STARTOVER_RESUME, CATCHUP_BEGINNING, CATCHUP_RESUME.
 */
export class SocuStreamingSessionImpl extends StreamingSessionImpl {
  protected readonly TAG: string = "SocuStreamingSessionImpl";

  public async createStreamingSession(
    sessionCreateModel: ISessionCreateModel
  ): Promise<void> {
    const { channel, drmType, transportEncodingType, watchAction } =
      sessionCreateModel;

    if (!channel) return;

    const { partnerStationId } = channel;

    const kernel =
      watchAction !== undefined &&
      watchAction !== null &&
      typeof watchAction === "object" &&
      "kernel" in watchAction
        ? (watchAction.kernel as Record<string, unknown> | undefined)
        : undefined;
    const kernelFields: Record<string, unknown> = kernel || {};

    const partnerId = kernelFields.partnerId as string | undefined;
    const partnerRootOfferId = kernelFields.partnerRootOfferId as
      | string
      | undefined;
    const partnerAssetId = kernelFields.partnerAssetId as string | undefined;

    if (!partnerId && !partnerRootOfferId && !partnerAssetId) {
      this.log(
        "error",
        "createStreamingSession(): missing required fields in watchAction"
      );
      this.mError = this.getPlaybackError("MISSING_PARTNER_PARAMS");
      this.mSessionState = SessionState.ERROR;
      return;
    }

    try {
      const tsn = await DeviceModel.getTiVoSettings().getBodyId();
      const sessionParams: TCreateSessionParams = {
        deviceType:
          DeviceModel.getTiVoSettings().getDeviceType() as TCreateSessionParams["deviceType"],
        drmType: (drmType || "widevine") as TCreateSessionParams["drmType"],
        partnerId: partnerId || "",
        partnerServicesCustomerId: await DeviceModel.getPartnerCustomerId(),
        partnerServicesBodyId: await DeviceModel.getCaDeviceId(),
        partnerStationId: partnerStationId || "",
        partnerRootOfferId: partnerRootOfferId || "",
        partnerAssetId: partnerAssetId || "",
        transportEncodingType:
          transportEncodingType as TCreateSessionParams["transportEncodingType"],
        tsn
      };

      this.log(
        "info",
        `createStreamingSession(): contentType=${sessionCreateModel.playbackContentType}`
      );

      const session = await new SessionInteractor().createSession(
        sessionParams
      );
      this.mDomainSession = session;
      this.mSession = {
        playlistUrl: SocuStreamingSessionImpl.resolvePlaylistUrl(
          session.playlistUrl,
          sessionCreateModel.playUri
        ),
        trickModeRestrictions:
          (session.trickModeRestrictions as Record<string, unknown>) || {},
        firstKeepAliveSeconds: session.firstKeepAliveSeconds,
        keepAliveIntervalSeconds: session.keepAliveIntervalSeconds || 0,
        localityId: SocuStreamingSessionImpl.resolveLocalityId(session)
      };
      this.mSessionState = SessionState.SUCCESS;
    } catch (error: unknown) {
      this.log("error", "createStreamingSession()", error);
      let errorMessage = "";
      if (typeof error === "object" && error !== null && "message" in error) {
        const errorObject = error as { message: unknown };
        if (typeof errorObject.message === "string") {
          errorMessage = errorObject.message;
        }
      }

      if (errorMessage === "Network unavailable") {
        this.mSessionState = SessionState.UNREACHABLE;
      } else {
        this.mError = this.getSessionError(
          error,
          PlaybackContentType.STARTOVER_BEGINNING
        );
        this.mSessionState = SessionState.ERROR;
      }
    }
  }
}
